/**
 * Utilidades para convertir datos a CSV/XLSX y descargar archivos
 * Versión mejorada con mejor compatibilidad con Excel
 */

/**
 * Convierte un array de objetos a formato CSV con mejor compatibilidad con Excel
 */
export const convertToCSV = (data: any[]): string => {
  if (!data || data.length === 0) {
    return '';
  }

  // Obtener headers del primer objeto
  const headers = Object.keys(data[0]);
  
  // Crear fila de headers
  const csvHeaders = headers.join(',');
  
  // Crear filas de datos
  const csvRows = data.map(row => {
    return headers.map(header => {
      let value = row[header];
      
      // Manejar valores null/undefined
      if (value === null || value === undefined) {
        return '';
      }
      
      // Convertir objetos/arrays a string
      if (typeof value === 'object') {
        value = JSON.stringify(value);
      }
      
      // Convertir a string y escapar comillas
      value = String(value).replace(/"/g, '""');
      
      // Agregar comillas si contiene comas, saltos de línea o comillas
      if (value.includes(',') || value.includes('\n') || value.includes('"')) {
        return `"${value}"`;
      }
      
      return value;
    }).join(',');
  });
  
  // Combinar headers y filas
  return [csvHeaders, ...csvRows].join('\n');
};

/**
 * Descarga un string como archivo
 */
export const downloadFile = (content: string, fileName: string, mimeType: string = 'text/csv;charset=utf-8;') => {
  // Crear un Blob con el contenido
  const blob = new Blob(['\uFEFF' + content], { type: mimeType }); // \uFEFF es BOM para UTF-8
  
  // Crear un enlace temporal
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  
  // Agregar al DOM, hacer clic y remover
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Limpiar el URL
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Formatea datos de usuarios para CSV
 */
export const formatUsersForCSV = (users: any[]): any[] => {
  return users.map(user => ({
    'ID': user.id,
    'Nombre': user.name,
    'Cédula': user.cedula,
    'Email': user.email || '',
    'Distribuidora': user.distribuidora || '',
    'Zona Geográfica': user.geographicZone || '',
    'Estado': user.status,
    'Rol': user.role,
    'Fecha de Registro': user.createdAt ? new Date(user.createdAt).toLocaleString('es-ES') : '',
    'Último Acceso': user.lastLogin ? new Date(user.lastLogin).toLocaleString('es-ES') : 'Nunca'
  }));
};

/**
 * Formatea datos de actividades para CSV
 */
export const formatActivitiesForCSV = (activities: any[]): any[] => {
  return activities.map(activity => ({
    'ID': activity.id,
    'Usuario': activity.user?.name || activity.userId,
    'Categoría': activity.category?.name || activity.categoryId,
    'Tipo de Actividad': activity.activityType,
    'Completada': activity.completed ? 'Sí' : 'No',
    'Fecha de Completado': activity.completionDate ? new Date(activity.completionDate).toLocaleString('es-ES') : '',
    'Tiempo Invertido (min)': activity.timeSpent ? Math.round(activity.timeSpent / 60) : 0,
    'Fecha de Inicio': activity.createdAt ? new Date(activity.createdAt).toLocaleString('es-ES') : ''
  }));
};

/**
 * Formatea datos de certificados para CSV
 */
export const formatCertificatesForCSV = (certificates: any[]): any[] => {
  return certificates.map(cert => ({
    'ID': cert.id,
    'Usuario': cert.user?.name || cert.userId,
    'Categoría': cert.category?.name || cert.categoryId,
    'Fecha de Emisión': cert.issuedDate ? new Date(cert.issuedDate).toLocaleString('es-ES') : '',
    'Descargado': cert.isDownloaded ? 'Sí' : 'No',
    'Veces Descargado': cert.downloadCount || 0,
    'URL': cert.certificateUrl || ''
  }));
};

/**
 * Genera nombre de archivo con timestamp
 */
export const generateFileName = (baseName: string, extension: string = 'csv'): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  return `${baseName}_${timestamp}.${extension}`;
};

/**
 * NUEVO: Convierte datos a formato Excel (XLSX) usando SheetJS
 * Esta función requiere la librería xlsx: npm install xlsx
 */
export const exportToExcel = async (
  data: any[],
  fileName: string,
  sheetName: string = 'Datos'
): Promise<void> => {
  try {
    // Importación dinámica de xlsx
    const XLSX = await import('xlsx');
    
    // Crear un nuevo workbook
    const wb = XLSX.utils.book_new();
    
    // Convertir datos a hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Obtener el rango de la hoja
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Calcular anchos de columna automáticos
    const colWidths: { wch: number }[] = [];
    const headers = Object.keys(data[0] || {});
    
    headers.forEach((header, colIndex) => {
      // Encontrar el ancho máximo para cada columna
      let maxWidth = header.length;
      
      for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
        const cellValue = String(data[rowIndex][header] || '');
        maxWidth = Math.max(maxWidth, cellValue.length);
      }
      
      // Limitar el ancho máximo a 50 caracteres
      colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
    });
    
    ws['!cols'] = colWidths;
    
    // Aplicar estilos a los headers (primera fila)
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[cellAddress]) continue;
      
      ws[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "001689" } }, // Color azul Brinsa
        alignment: { horizontal: "center", vertical: "center" }
      };
    }
    
    // Agregar la hoja al workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    
    // Generar archivo y descargar
    XLSX.writeFile(wb, fileName);
    
  } catch (error) {
    console.error('Error exportando a Excel:', error);
    throw new Error('No se pudo exportar a Excel. Asegúrate de tener instalada la librería xlsx.');
  }
};

/**
 * MEJORADO: Exporta a CSV con delimitador de punto y coma (mejor para Excel)
 */
export const convertToCSVExcelFriendly = (data: any[]): string => {
  if (!data || data.length === 0) {
    return '';
  }

  // Usar punto y coma como delimitador (mejor para Excel en español)
  const delimiter = ';';
  
  // Obtener headers del primer objeto
  const headers = Object.keys(data[0]);
  
  // Crear fila de headers
  const csvHeaders = headers.join(delimiter);
  
  // Crear filas de datos
  const csvRows = data.map(row => {
    return headers.map(header => {
      let value = row[header];
      
      // Manejar valores null/undefined
      if (value === null || value === undefined) {
        return '';
      }
      
      // Convertir objetos/arrays a string
      if (typeof value === 'object') {
        value = JSON.stringify(value);
      }
      
      // Convertir a string y escapar comillas
      value = String(value).replace(/"/g, '""');
      
      // Agregar comillas si contiene el delimitador, saltos de línea o comillas
      if (value.includes(delimiter) || value.includes('\n') || value.includes('"')) {
        return `"${value}"`;
      }
      
      return value;
    }).join(delimiter);
  });
  
  // Combinar headers y filas
  return [csvHeaders, ...csvRows].join('\n');
};

/**
 * Función principal MEJORADA para exportar datos (soporta CSV y Excel)
 */
export const exportDataToCSV = (
  data: any[], 
  type: 'users' | 'activities' | 'certificates',
  dateRange?: string,
  format: 'csv' | 'excel' = 'csv' // NUEVO: permite elegir formato
): void => {
  let formattedData: any[];
  let baseFileName: string;
  let sheetName: string;
  
  switch (type) {
    case 'users':
      formattedData = formatUsersForCSV(data);
      baseFileName = `usuarios_${dateRange || 'todos'}`;
      sheetName = 'Usuarios';
      break;
    case 'activities':
      formattedData = formatActivitiesForCSV(data);
      baseFileName = `actividades_${dateRange || 'todas'}`;
      sheetName = 'Actividades';
      break;
    case 'certificates':
      formattedData = formatCertificatesForCSV(data);
      baseFileName = `certificados_${dateRange || 'todos'}`;
      sheetName = 'Certificados';
      break;
    default:
      throw new Error('Tipo de exportación no válido');
  }
  
  if (formattedData.length === 0) {
    throw new Error('No hay datos para exportar');
  }
  
  if (format === 'excel') {
    // Exportar a Excel
    const fileName = generateFileName(baseFileName, 'xlsx');
    exportToExcel(formattedData, fileName, sheetName);
  } else {
    // Exportar a CSV con punto y coma (mejor para Excel)
    const csvContent = convertToCSVExcelFriendly(formattedData);
    const fileName = generateFileName(baseFileName, 'csv');
    downloadFile(csvContent, fileName, 'text/csv;charset=utf-8;');
  }
};

/**
 * ALTERNATIVA: Función específica para exportar directamente a Excel
 */
export const exportDataToExcel = async (
  data: any[], 
  type: 'users' | 'activities' | 'certificates',
  dateRange?: string
): Promise<void> => {
  return exportDataToCSV(data, type, dateRange, 'excel');
};

/**
 * Genera un reporte unificado: usuarios + actividades + certificados
 * en un solo archivo Excel, cruzando data por userId
 */
export const exportUnifiedReport = async (
  users: any[],
  activities: any[],
  certificates: any[],
  dateRange?: string
): Promise<void> => {
  const XLSX = await import('xlsx');

  // 1. Agrupar actividades por userId + categoría
  //    Para cada usuario y categoría, consolidar los tipos de actividad
  const activityMap = new Map<string, Map<string, any>>();
  
  for (const act of activities) {
    const uid = act.userId || act.user?.id;
    if (!uid) continue;
    
    const catName = act.category?.name || act.categoryName || 'Sin categoría';
    const key = `${uid}__${catName}`;
    
    if (!activityMap.has(uid)) {
      activityMap.set(uid, new Map());
    }
    
    const userActivities = activityMap.get(uid)!;
    
    if (!userActivities.has(catName)) {
      userActivities.set(catName, {
        categoria: catName,
        test: 'NO',
        ar: 'NO',
        video: 'NO',
        portfolio: 'NO',
        completada: 'No',
        fechaCompletado: '',
        tiempoInvertido: 0,
        fechaInicio: '',
      });
    }
    
    const entry = userActivities.get(catName)!;
    const type = (act.activityType || '').toLowerCase();
    
    if (type === 'test') entry.test = 'SI';
    if (type === 'ar') entry.ar = 'SI';
    if (type === 'video') entry.video = 'SI';
    if (type === 'portfolio') entry.portfolio = 'SI';
    
    if (act.completed) entry.completada = 'Sí';
    
    if (act.completionDate) {
      entry.fechaCompletado = new Date(act.completionDate).toLocaleString('es-ES');
    }
    
    entry.tiempoInvertido += (act.timeSpent || 0);
    
    if (act.createdAt && !entry.fechaInicio) {
      entry.fechaInicio = new Date(act.createdAt).toLocaleString('es-ES');
    }
  }

  // 2. Agrupar certificados por userId
  const certMap = new Map<string, any[]>();
  
  for (const cert of certificates) {
    const uid = cert.userId || cert.user?.id;
    if (!uid) continue;
    
    if (!certMap.has(uid)) {
      certMap.set(uid, []);
    }
    
    certMap.get(uid)!.push({
      categoria: cert.category?.name || cert.categoryName || 'Sin categoría',
      fechaEmision: cert.issuedDate ? new Date(cert.issuedDate).toLocaleString('es-ES') : '',
      descargado: cert.isDownloaded ? 'Sí' : 'No',
      vecesDescargado: cert.downloadCount || 0,
    });
  }

  // 3. Construir filas unificadas
  const rows: any[] = [];
  
  for (const user of users) {
    const uid = user.id;
    const userBase = {
      'ID': uid,
      'Nombre': user.name || '',
      'Cédula': user.cedula || '',
      'Email': user.email || '',
      'Distribuidora': user.distribuidora || '',
      'Zona Geográfica': user.geographicZone || '',
      'Estado': user.status || '',
      'Rol': user.role || '',
      'Fecha de Registro': user.createdAt ? new Date(user.createdAt).toLocaleString('es-ES') : '',
      'Último Acceso': user.lastLogin ? new Date(user.lastLogin).toLocaleString('es-ES') : 'Nunca',
    };

    const userActs = activityMap.get(uid);
    const userCerts = certMap.get(uid) || [];
    const actEntries = userActs ? Array.from(userActs.values()) : [];

    // Determinar cuántas filas necesita este usuario
    const maxRows = Math.max(1, actEntries.length, userCerts.length);

    for (let i = 0; i < maxRows; i++) {
      const act = actEntries[i];
      const cert = userCerts[i];

      rows.push({
        ...userBase,
        // Actividades
        'Act. Categoría': act?.categoria || '',
        'Test': act?.test || '',
        'AR': act?.ar || '',
        'Video': act?.video || '',
        'Portafolio': act?.portfolio || '',
        'Act. Completada': act?.completada || '',
        'Fecha Completado': act?.fechaCompletado || '',
        'Tiempo Invertido (min)': act?.tiempoInvertido || '',
        'Fecha Inicio': act?.fechaInicio || '',
        // Certificados
        'Cert. Categoría': cert?.categoria || '',
        'Fecha Emisión': cert?.fechaEmision || '',
        'Descargado': cert?.descargado || '',
        'Veces Descargado': cert?.vecesDescargado || '',
      });
    }
  }

  if (rows.length === 0) {
    throw new Error('No hay datos para exportar');
  }

  // 4. Generar Excel
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);

  // Auto-anchos de columna
  const colWidths = Object.keys(rows[0]).map(key => {
    const maxLength = Math.max(
      key.length,
      ...rows.map(row => String(row[key] || '').length)
    );
    return { wch: Math.min(maxLength + 2, 40) };
  });
  ws['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, 'Reporte Unificado');

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  XLSX.writeFile(wb, `reporte_unificado_${dateRange || 'todos'}_${timestamp}.xlsx`);
};